import {
  makeObservable,
  observable,
  computed,
  action,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
  ObservableMap,
  reaction,
  IReactionDisposer,
} from "mobx";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { SyncModelKind } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import { UploadedFileBatchModelData } from "./types";
import { UploadedFileBatchObservable } from "./UploadedFileBatchObservable";
import { SyncUpdateValue } from "@/store/sync/types";
import { Maybe } from "@/domains/common/types";
import { Dexie, liveQuery, Subscription } from "dexie";
import { searchUploadedFileBatches } from "@/store/uploaded-files/search-uploaded-file-batches";
import { CreateUploadedFileBatchOperation } from "@/store/sync/operations/uploaded-file/CreateUploadedFileBatchOperation";
import { uuidModule } from "@/modules/uuid";
import { Uuid } from "@/domains/global/identifiers";
import { StartProcessingUploadedFileBatchOperation } from "@/store/sync/operations/uploaded-file/StartProcessingUploadedFileBatchOperation";
import { UpdateUploadedFileBatchOperation } from "@/store/sync/operations/uploaded-file/UpdateUploadedFileBatchOperation";
import { toastModule } from "@/modules/toast";

export class AppStoreUploadedFileBatchStore extends BaseSyncModelStore<
  UploadedFileBatchObservable,
  UploadedFileBatchModelData
> {
  liveQuerySubscription: Maybe<Subscription>;
  uploadedFileBatches: UploadedFileBatchObservable[] = [];
  uploadedFileBatchConfigs = new ObservableMap<Uuid, Record<string, unknown>>();
  uploadBatchMap = new ObservableMap<Uuid, Uuid>();
  private initialLoaded: boolean = false;

  // Track batches that should navigate to imported notes when processing completes
  private pendingNavigationBatchIds = new Set<Uuid>();
  private batchCompletionReactions = new Map<Uuid, IReactionDisposer>();

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.UploadedFileBatch, ...injectedDeps });
    makeObservable<
      this,
      | "subscribeLiveQuery"
      | "unsubscribeLiveQuery"
      | "setIsReady"
      | "initialLoaded"
      | "uploadBatchMap"
      | "getBatchConfig"
      | "pendingNavigationBatchIds"
      | "batchCompletionReactions"
    >(this, {
      startProcessingBatch: true,
      getUploadFileIdsForBatch: true,
      addUploadToBatch: true,
      addUploadsToBatch: true,
      searchUploadedFileBatches: true,
      getBatchIdForSession: true,
      createSyncModel: false,
      navigateToImportedNotesOnCompletion: false,

      uploadedFileBatchConfigs: observable,
      uploadedFileBatches: observable,
      liveQuerySubscription: observable,
      subscribeLiveQuery: action,
      unsubscribeLiveQuery: action,
      uploadBatchMap: observable,
      initialLoaded: observable,
      getBatchConfig: observable,
      pendingNavigationBatchIds: false,
      batchCompletionReactions: false,

      setIsReady: action,
      createUploadedFileBatch: action,
      updateUploadedFileBatch: action,

      aBatchIsUploading: computed,
      isReady: computed,
    });

    onBecomeObserved(this, "uploadedFileBatches", () => this.subscribeLiveQuery());
    onBecomeObserved(this, "uploadBatchMap", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "uploadedFileBatches", () => this.unsubscribeLiveQuery());
    onBecomeUnobserved(this, "uploadBatchMap", () => this.unsubscribeLiveQuery());
    onBecomeObserved(this, "isReady", () => this.subscribeLiveQuery());
  }

  get isReady() {
    return this.initialLoaded;
  }

  get aBatchIsUploading() {
    let isDone = true;
    for (const key of this.uploadBatchMap.keys()) {
      if (this.store.uploadProgress.getFileResult(key) === undefined) {
        isDone = false;
        break;
      }
    }

    return !isDone;
  }

  createSyncModel(data: SyncUpdateValue<UploadedFileBatchModelData>): UploadedFileBatchObservable {
    return new UploadedFileBatchObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  public searchUploadedFileBatches = async (): Promise<UploadedFileBatchObservable[]> => {
    const ids = await Dexie.waitFor(searchUploadedFileBatches({ store: this.store }));
    return this.getMany(ids);
  };

  public async getBatchIdForSession(sessionId: string): Promise<string | null> {
    const batch = await this.localTable
      .filter(b => b.model_data.associated_session_id === sessionId)
      .first();
    return batch?.model_id ?? null;
  }

  /**
   * Creates a new uploaded file batch.
   *
   * @param config Optional configuration JSON for the batch
   * @returns The ID of the newly created file batch
   */
  public async createUploadedFileBatch({
    sessionId,
    config = {},
  }: {
    sessionId: string;
    config?: Record<string, unknown>;
  }): Promise<string> {
    const batchId = uuidModule.generate();
    this.uploadedFileBatchConfigs.set(batchId, config);

    await new CreateUploadedFileBatchOperation({
      store: this.store,
      payload: {
        id: batchId,
        session_id: sessionId,
        config_json: config,
        processing_started_at: null,
        processing_ended_at: null,
      },
    }).execute();

    return batchId;
  }

  /**
   * Update an upload file batch.
   *
   * @param batchId
   * @param sessionId
   * @param config
   * @returns The ID of the newly created file batch
   */
  public async updateUploadedFileBatch({
    batchId,
    config,
  }: {
    batchId: Uuid;
    config: Record<string, unknown>;
  }) {
    const previous_config = this.getBatchConfig(batchId);
    const new_config = { ...previous_config, ...config };
    this.uploadedFileBatchConfigs.set(batchId, new_config);

    await new UpdateUploadedFileBatchOperation({
      store: this.store,
      payload: {
        id: batchId,
        config_json: new_config,
      },
    }).execute();

    return batchId;
  }

  public addUploadToBatch(batchId: string, fileId: string) {
    this.uploadBatchMap.set(fileId, batchId);
  }

  public getUploadFileIdsForBatch(batchId: string) {
    return Array.from(this.uploadBatchMap.entries())
      .filter(([_, bid]) => bid === batchId)
      .map(([fileId]) => fileId);
  }

  public addUploadsToBatch(batchId: string, fileIds: string[]) {
    fileIds.forEach(fileId => this.addUploadToBatch(batchId, fileId));
  }

  public async startProcessingBatch(batchId: Uuid, sessionId: Uuid) {
    await new StartProcessingUploadedFileBatchOperation({
      store: this.store,
      payload: {
        id: batchId,
        session_id: sessionId,
      },
    }).execute();
  }

  /**
   * Register a batch to navigate to imported notes when processing completes.
   * Sets up a MobX reaction to watch for the batch's isDoneProcessing state.
   */
  public navigateToImportedNotesOnCompletion(batchId: Uuid) {
    // Avoid duplicate registrations
    if (this.pendingNavigationBatchIds.has(batchId)) {
      return;
    }

    this.pendingNavigationBatchIds.add(batchId);

    // Set up a reaction to watch for batch completion
    const dispose = reaction(
      () => {
        const batch = this.get(batchId);
        return batch?.isDoneProcessing ?? false;
      },
      isDone => {
        if (isDone && this.pendingNavigationBatchIds.has(batchId)) {
          toastModule.triggerToast({
            content: `Import complete`,
          });

          // Navigate to imported notes
          this.store.navigation.goToImportBatchNotes({
            importBatchId: batchId,
          });

          // Clean up
          this.pendingNavigationBatchIds.delete(batchId);
          const reactionDispose = this.batchCompletionReactions.get(batchId);
          if (reactionDispose) {
            reactionDispose();
            this.batchCompletionReactions.delete(batchId);
          }
        }
      },
      { fireImmediately: true }
    );

    this.batchCompletionReactions.set(batchId, dispose);
  }

  private subscribeLiveQuery() {
    this.setIsReady(false);
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(() => {
      return this.searchUploadedFileBatches();
    }).subscribe({
      next: items => {
        runInAction(() => {
          this.uploadedFileBatches = items;
          this.setIsReady(true);
        });
      },
    });
  }

  private unsubscribeLiveQuery() {
    this.setIsReady(false);
    this.liveQuerySubscription?.unsubscribe();
  }

  private setIsReady(value: boolean) {
    this.initialLoaded = value;
  }

  private getBatchConfig(batchId: string) {
    const config = this.uploadedFileBatchConfigs.get(batchId);

    if (!config) {
      const batch = this.get(batchId);
      const configJson = batch?.modelData.config_json;
      if (configJson) {
        this.uploadedFileBatchConfigs.set(batchId, configJson);
        return configJson;
      }
    }

    return config;
  }
}
